import { useState } from "react";
import {
  LEAD_ASSIGNABLE_ROLES,
  type LeadAssignmentMember,
  type LeadAssignmentPolicy,
} from "@school-sales-ai/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { RoleBadge } from "@/components/RoleBadge";
import {
  useLeadAssignmentPolicy,
  useSetLeadAssignmentPolicy,
  useSetReceivesLeads,
} from "@/hooks/useLeadAssignment";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** One switch with its explanation — label and help on the left, switch on the right. */
function PolicyToggle({
  id,
  label,
  help,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  help: React.ReactNode;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-[13px] font-medium text-ink-900">
          {label}
        </label>
        <div className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">{help}</div>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={label}
      />
    </div>
  );
}

/** Who the round-robin may hand new leads to: one switch per active teammate. */
function RotationMembers({
  members,
  canManage,
}: {
  members: LeadAssignmentMember[];
  canManage: boolean;
}): React.JSX.Element {
  const setReceivesLeads = useSetReceivesLeads();
  const team = members.filter((m) => m.active && LEAD_ASSIGNABLE_ROLES.includes(m.role));
  const inRotation = team.filter((m) => m.receivesLeads).length;

  return (
    <div className="rounded-lg border border-ink-100 bg-ink-50/40 p-3">
      <p className="text-[13px] font-medium text-ink-900">Quién recibe prospectos</p>
      <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">
        Apaga a quien no deba recibir prospectos nuevos, por ejemplo durante sus
        vacaciones. Sus prospectos actuales se quedan con esa persona; transfiérelos si
        hace falta. Para sacar a alguien del equipo por completo, desactiva su cuenta en
        Usuarios: sus prospectos quedan sin asignar.
      </p>
      {team.length === 0 ? (
        <p className="mt-3 text-[12.5px] text-ink-500">Todavía no hay miembros activos.</p>
      ) : (
        <ul className="mt-3 divide-y divide-ink-100">
          {team.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[13px] text-ink-900">{m.name}</span>
                  <RoleBadge role={m.role} />
                </div>
                <p className="text-[11.5px] text-ink-400">
                  {m.assignedLeadCount === 1
                    ? "1 prospecto asignado"
                    : `${String(m.assignedLeadCount)} prospectos asignados`}
                </p>
              </div>
              <Switch
                checked={m.receivesLeads}
                disabled={!canManage || setReceivesLeads.isPending}
                onCheckedChange={(checked) =>
                  setReceivesLeads.mutate({ profileId: m.id, receivesLeads: checked })
                }
                aria-label={`${m.name} recibe prospectos`}
              />
            </li>
          ))}
        </ul>
      )}
      {team.length > 0 && inRotation === 0 ? (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
          Nadie está recibiendo prospectos: los nuevos quedarán sin asignar hasta que un
          administrador los reparta.
        </p>
      ) : null}
    </div>
  );
}

function PolicyBody({ policy }: { policy: LeadAssignmentPolicy }): React.JSX.Element {
  const setPolicy = useSetLeadAssignmentPolicy();
  const [confirmOff, setConfirmOff] = useState(false);
  const disabled = !policy.canManage || setPolicy.isPending;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-ink-100 bg-white p-4">
        <PolicyToggle
          id="lead-assignment-enabled"
          label="Asignar prospectos a miembros específicos del equipo"
          help={
            <>
              Cada prospecto puede tener un responsable. Los miembros solo verán sus
              propios prospectos y, según la configuración de abajo, los que todavía no
              tienen responsable. Los administradores ven todos, salvo que actives la
              última opción de abajo. Si está apagado, todo el equipo ve y atiende a
              todos los prospectos, como hasta ahora.
            </>
          }
          checked={policy.enabled}
          disabled={disabled}
          onChange={(checked) => {
            // Turning it off widens what every member sees — confirm first.
            if (!checked) {
              setConfirmOff(true);
              return;
            }
            setPolicy.mutate({ enabled: true });
          }}
        />

        {policy.enabled ? (
          <div className="mt-4 flex flex-col gap-4 border-l-2 border-ink-100 pl-4">
            <PolicyToggle
              id="lead-assignment-auto"
              label="El agente asigna prospectos automáticamente"
              help={
                <>
                  <span className="block">
                    <strong className="font-medium text-ink-700">Apagado:</strong> los
                    miembros ven los prospectos sin responsable y se los pueden asignar
                    ellos mismos, con el botón «Asignarme» o al responderles desde el CRM.
                  </span>
                  <span className="mt-1 block">
                    <strong className="font-medium text-ink-700">Encendido:</strong> el
                    agente reparte cada prospecto, por turnos, entre los miembros que
                    reciben prospectos, en cuanto escribe por primera vez. Cada miembro
                    solo ve los suyos; los que se queden sin asignar los reparte un
                    administrador.
                  </span>
                </>
              }
              checked={policy.autoAssign}
              disabled={disabled}
              onChange={(checked) => setPolicy.mutate({ autoAssign: checked })}
            />

            {policy.autoAssign ? (
              <RotationMembers members={policy.members} canManage={policy.canManage} />
            ) : null}

            <PolicyToggle
              id="lead-assignment-transfer"
              label="Los miembros del equipo pueden transferir prospectos"
              help={
                <>
                  Permite que un miembro pase uno de sus propios prospectos a otro
                  compañero desde la ficha del prospecto. Los administradores siempre
                  pueden transferir prospectos de un miembro a otro, aunque esta opción
                  esté apagada.
                </>
              }
              checked={policy.memberTransfer}
              disabled={disabled}
              onChange={(checked) => setPolicy.mutate({ memberTransfer: checked })}
            />

            <PolicyToggle
              id="lead-assignment-admin-own-only"
              label="Los administradores solo ven sus propios prospectos"
              help={
                <>
                  <span className="block">
                    Para un equipo de administradores que se reparte el trabajo: cada
                    administrador ve los prospectos asignados a él y los que todavía no
                    tienen responsable. Los prospectos de un miembro solo los ve ese
                    miembro.
                  </span>
                  <span className="mt-1 block">
                    Al responder o crear un prospecto sin responsable, queda asignado a
                    quien lo atendió. Es un filtro de trabajo, no de privacidad: cualquier
                    administrador puede apagarlo.
                  </span>
                </>
              }
              checked={policy.adminOwnLeadsOnly}
              disabled={disabled}
              onChange={(checked) => setPolicy.mutate({ adminOwnLeadsOnly: checked })}
            />
          </div>
        ) : null}
      </div>

      {policy.updatedAt ? (
        <p className="text-xs text-ink-500">
          Último cambio
          {policy.updatedByName ? ` por ${policy.updatedByName}` : ""} el{" "}
          {formatDate(policy.updatedAt)}.
        </p>
      ) : null}
      {!policy.canManage ? (
        <p className="text-xs text-ink-500">
          Solo un administrador puede cambiar esta política.
        </p>
      ) : null}

      <AlertDialog open={confirmOff} onOpenChange={(open) => !open && setConfirmOff(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar la asignación de prospectos?</AlertDialogTitle>
            <AlertDialogDescription>
              Todo el equipo volverá a ver a todos los prospectos. Los responsables
              asignados se conservan por si la vuelves a activar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setPolicy.mutate({ enabled: false });
                setConfirmOff(false);
              }}
            >
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Agente → «Políticas de asignación de prospectos».
 *
 * Every switch saves the moment it is flipped (the `BroadcastAuthorizationCard`
 * pattern), outside the Ajustes tab's Guardar cycle: each one changes what
 * other people can see, and a pending unsaved state for that would be a trap.
 * Rendered for every role, disabled unless the server says this user may
 * manage it — a member seeing the rule is the answer to "why can't I see that
 * prospect?".
 */
export function LeadAssignmentSettings(): React.JSX.Element | null {
  const { data: policy, isPending } = useLeadAssignmentPolicy();
  if (isPending || !policy) return null;
  return <PolicyBody policy={policy} />;
}
